import { useState, useEffect, useCallback } from "react";
import { dashboardApi, analyticsApi, exportApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Users, ShoppingCart, Target, ArrowUpRight, ArrowDownRight,
  MessageCircle, Clock, ChevronRight, Sparkles,
  BarChart3, Download,
  ArrowRight,
} from "lucide-react";
import DateRangePicker from "../components/DateRangePicker";
import CategoryIntentWidget from "../components/CategoryIntentWidget";
import RecoveredRevenueWidget from "../components/RecoveredRevenueWidget";
import WhyCustomersLeaveWidget from "../components/WhyCustomersLeaveWidget";
import AnimatedNumber from "../components/AnimatedNumber";
import { format } from "date-fns";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  CartesianGrid,
} from "recharts";

const CAT_COLORS = {
  "Sculptures": { color: "#FC5C1C" },
  "Books": { color: "#3B82F6" },
  "Paintings": { color: "#8866E7" },
  "Clothing": { color: "#14B8A6" },
  "Jewelry": { color: "#EC4899" },
  "Home and Living": { color: "#22C55E" },
  "Other": { color: "#94A3B8" },
};

const CHART_MARGIN = { top: 5, right: 5, bottom: 5, left: 5 };
const CAT_BAR_GRID_STROKE = "#FFEDD5";
const DASH_AXIS_TICK = { fontSize: 10, fill: "#A8A29E" };
const DASH_TOOLTIP_STYLE = {
  background: "white",
  border: "1px solid var(--border)",
  borderRadius: "14px",
  boxShadow: "0 8px 24px rgba(28,25,23,0.08)",
  fontSize: 12,
  padding: "8px 12px",
};

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [analytics, setAnalytics] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateRange?.from) params.date_from = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.date_to = format(dateRange.to, "yyyy-MM-dd");
      const [statsRes, analyticsRes] = await Promise.all([
        dashboardApi.getStats(params),
        analyticsApi.getDashboard(params),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleExport = async (type) => {
    try {
      const params = {};
      if (dateRange?.from) params.date_from = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.date_to = format(dateRange.to, "yyyy-MM-dd");
      const res = await exportApi[type](params);
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type} exported successfully`);
    } catch (e) { toast.error("Export failed"); }
  };

  const { greeting, accentWord } = (() => {
    const h = new Date().getHours();
    if (h < 12) return { greeting: "Good", accentWord: "morning" };
    if (h < 17) return { greeting: "Good", accentWord: "afternoon" };
    return { greeting: "Good", accentWord: "evening" };
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-[--accent] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[--text-muted]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const categoryData = stats?.category_revenue
    ? Object.entries(stats.category_revenue).map(([name, value]) => ({
        name, value, color: CAT_COLORS[name]?.color || "#94A3B8"
      })).sort((a, b) => b.value - a.value)
    : [];

  // Insight delta: prefer revenue_trend if available, else leads_trend
  const insightDelta = stats?.revenue_trend ?? stats?.leads_trend ?? null;
  const deltaClass = insightDelta == null ? "flat" : insightDelta > 0 ? "up" : insightDelta < 0 ? "down" : "flat";

  return (
    <div data-testid="dashboard-page" className="dash-canvas space-y-10">
      {/* ─── HERO — editorial greeting + today insight ─── */}
      <section className="dash-hero animate-fade-up" data-testid="dash-hero">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 relative z-10">
          <div className="max-w-2xl">
            <p className="dash-hero-eyebrow">{format(new Date(), "EEEE • MMM d, yyyy")}</p>
            <h1 className="dash-hero-title">
              {greeting} <span className="accent-word">{accentWord}</span>,
              <br />
              {user?.name?.split(" ")[0] || "Admin"}.
            </h1>
            <p className="dash-hero-subtitle">
              Here's a quiet look at how the studio is performing today. Numbers, intent, and the
              conversations that matter — all in one place.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
            </div>
          </div>

          {/* Today insight card */}
          <div className="dash-insight-card" data-testid="dash-insight-card">
            <p className="dash-insight-label">Revenue this period</p>
            <div className="dash-insight-value">
              <AnimatedNumber
                value={stats?.total_revenue || 0}
                format={(n) => formatCurrency(Math.round(n))}
              />
            </div>
            {insightDelta != null && (
              <span className={`dash-insight-delta ${deltaClass}`}>
                {insightDelta > 0 ? <ArrowUpRight size={12} /> : insightDelta < 0 ? <ArrowDownRight size={12} /> : null}
                {Math.abs(insightDelta).toFixed(1)}% vs previous
              </span>
            )}
            {insightDelta == null && (
              <span className="dash-insight-delta flat">
                <Sparkles size={12} /> live snapshot
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ─── KPI STRIP — editorial cream cards ─── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <EditorialStat
          icon={<Users size={16} />}
          label="Total Leads"
          value={stats?.total_leads || 0}
          trend={stats?.leads_trend}
          stagger={1}
        />
        <EditorialStat
          icon={<Target size={16} />}
          label="Conversion"
          value={`${(stats?.conversion_rate || 0).toFixed(1)}%`}
          stagger={2}
        />
        <EditorialStat
          icon={<ShoppingCart size={16} />}
          label="Orders"
          value={stats?.total_orders || 0}
          stagger={3}
        />
        <EditorialStat
          icon={<TrendingUp size={16} />}
          label="Revenue"
          value={formatCurrency(stats?.total_revenue || 0)}
          stagger={4}
        />
      </section>

      {/* Category Intent, recovered revenue, why customers leave — preserved */}
      <CategoryIntentWidget />
      <RecoveredRevenueWidget />
      <WhyCustomersLeaveWidget />

      {/* ─── QUICK ACTIONS ─── */}
      <section>
        <div className="flex flex-wrap gap-2.5">
          {[
            { icon: <MessageCircle size={14} />, label: "Open Chats", onClick: () => navigate("/chats") },
            { icon: <Users size={14} />, label: "Manage Leads", onClick: () => navigate("/leads") },
            { icon: <BarChart3 size={14} />, label: "Performance", onClick: () => navigate("/performance") },
            { icon: <Sparkles size={14} />, label: "AI Features", onClick: () => navigate("/chats") },
          ].map((a, i) => (
            <button
              key={a.label}
              onClick={a.onClick}
              className="quick-action-chip"
              data-testid={`quick-action-${i}`}
            >
              {a.icon} {a.label}
              <ChevronRight size={12} className="opacity-50 group-hover:opacity-100 transition-opacity" />
            </button>
          ))}
        </div>
      </section>

      {/* ─── ACTIVITY + COMPARISON ─── */}
      <section>
        <div className="dash-section-head">
          <div>
            <p className="dash-section-eyebrow">Activity</p>
            <h2 className="dash-section-title">What just happened</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Bar chart */}
          <div className="dash-card">
            <div className="dash-card-head">
              <div>
                <h3>Category comparison</h3>
                <p>Visual revenue across product lines</p>
              </div>
            </div>
            <div className="dash-card-body">
              {categoryData.length > 0 ? (
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={categoryData} margin={CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke={CAT_BAR_GRID_STROKE} vertical={false} />
                      <XAxis dataKey="name" tick={DASH_AXIS_TICK} axisLine={false} tickLine={false} />
                      <YAxis tick={DASH_AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={v => formatCompact(v)} />
                      <Tooltip
                        contentStyle={DASH_TOOLTIP_STYLE}
                        formatter={(v) => [`INR ${v.toLocaleString()}`, "Revenue"]}
                      />
                      <Bar dataKey="value" radius={[10, 10, 0, 0]} maxBarSize={44}>
                        {categoryData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState icon={<BarChart3 />} text="No data" />
              )}
            </div>
          </div>

          {/* Recent leads — timeline-style */}
          <div className="dash-card">
            <div className="dash-card-head">
              <div>
                <h3>Recent activity</h3>
                <p>Latest leads and orders</p>
              </div>
              <button onClick={() => navigate("/leads")} className="text-[11px] text-[--accent] font-medium hover:underline flex items-center gap-1 mt-1">
                View all <ChevronRight size={12} />
              </button>
            </div>
            <div>
              {stats?.recent_leads?.length > 0 ? (
                stats.recent_leads.slice(0, 6).map((lead, i) => (
                  <div key={lead.id || i}
                    className="dash-activity-row"
                    onClick={() => navigate("/chats")}
                    data-testid={`recent-lead-${i}`}>
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-sm ${
                      ["bg-orange-500", "bg-purple-500", "bg-blue-500", "bg-emerald-500", "bg-pink-500", "bg-teal-500"][i % 6]
                    }`}>
                      {lead.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[--text-primary] truncate">{lead.name}</p>
                      <p className="text-[11px] text-[--text-muted]">{lead.phone}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        lead.status === "placed_order" ? "bg-green-50 text-green-700" :
                        lead.status === "contacted" ? "bg-blue-50 text-blue-700" :
                        lead.status === "qualified" ? "bg-purple-50 text-purple-700" :
                        lead.status === "closed" ? "bg-gray-100 text-gray-600" :
                        "bg-amber-50 text-amber-700"
                      }`}>{lead.status || "new"}</span>
                      {lead.category && (
                        <p className="text-[10px] text-[--text-muted] mt-0.5">{lead.category}</p>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-6 py-10">
                  <EmptyState icon={<Clock />} text="No recent activity" />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Conversion Funnel */}
      {analytics?.funnel && (
        <section className="dash-card" data-testid="funnel-card">
          <div className="dash-card-head">
            <div>
              <p className="dash-section-eyebrow" style={{ marginBottom: 6 }}>Funnel</p>
              <h3>From first hello to checkout</h3>
              <p>Lead journey across the entire pipeline</p>
            </div>
          </div>
          <div className="dash-card-body">
            <div className="flex items-center justify-between gap-3">
              {[
                { label: "Total Leads", value: analytics.funnel.total, color: "#3B82F6", bg: "bg-blue-50" },
                { label: "Active", value: analytics.funnel.active, color: "#F59E0B", bg: "bg-amber-50" },
                { label: "Ordered", value: analytics.funnel.ordered, color: "#8B5CF6", bg: "bg-purple-50" },
                { label: "Converted", value: analytics.funnel.converted, color: "#22C55E", bg: "bg-green-50" },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-2 flex-1">
                  <div className={`${step.bg} rounded-2xl p-4 flex-1 text-center border border-white/40`} data-testid={`funnel-${step.label.toLowerCase()}`}>
                    <p className="text-2xl leading-none" style={{ color: step.color, fontFamily: "Playfair Display, serif" }}>
                      {step.value}
                    </p>
                    <p className="text-[10px] text-[--text-muted] font-medium mt-2 uppercase tracking-[0.14em]">{step.label}</p>
                    {i > 0 && arr[i-1].value > 0 && (
                      <p className="text-[9px] font-semibold mt-1.5" style={{ color: step.color }}>
                        {((step.value / arr[i-1].value) * 100).toFixed(1)}%
                      </p>
                    )}
                  </div>
                  {i < arr.length - 1 && <ArrowRight size={16} className="text-[--accent-soft] flex-shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Export */}
      <section className="dash-card" data-testid="export-card">
        <div className="dash-card-head">
          <div>
            <p className="dash-section-eyebrow" style={{ marginBottom: 6 }}>Reports</p>
            <h3 className="flex items-center gap-2"><Download size={16} className="text-[--accent]" /> Export data</h3>
            <p>Download CSV snapshots of leads, orders, and performance</p>
          </div>
        </div>
        <div className="dash-card-body">
          <div className="flex flex-wrap gap-2.5">
            {[
              { label: "Export Leads (CSV)", fn: () => handleExport("leads"), testId: "export-leads" },
              { label: "Export Orders (CSV)", fn: () => handleExport("orders"), testId: "export-orders" },
              ...(user?.role === "admin" || user?.role === "super_admin" ? [{ label: "Export Performance (CSV)", fn: () => handleExport("performance"), testId: "export-performance" }] : []),
            ].map(e => (
              <button key={e.testId} onClick={e.fn} className="quick-action-chip" data-testid={e.testId}>
                <Download size={12} /> {e.label}
              </button>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

/* Editorial Stat — with animated number counter */
const EditorialStat = ({ icon, label, value, trend, stagger = 1 }) => {
  const isNumeric = typeof value === "number";
  return (
    <div
      className={`editorial-stat animate-fade-up stagger-${stagger}`}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="editorial-stat-icon">{icon}</div>
        {trend != null && (
          <div
            className={`flex items-center gap-0.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${
              trend >= 0
                ? "bg-[var(--accent-pale)] text-[var(--accent)]"
                : "bg-red-50 text-red-500"
            }`}
          >
            {trend >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div
        className="text-[34px] text-[var(--text-primary)] tracking-tight leading-none tabular-nums"
        style={{ fontFamily: "Playfair Display, serif" }}
      >
        {isNumeric
          ? <AnimatedNumber value={value} format={(n) => Math.round(n).toLocaleString()} />
          : value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--text-secondary)] mt-3 font-medium">
        {label}
      </div>
    </div>
  );
};

const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center py-8 text-[--text-muted]">
    <span className="opacity-30 mb-2">{icon}</span>
    <p className="text-xs">{text}</p>
  </div>
);

function formatCurrency(n) {
  if (n >= 10000000) return `INR ${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `INR ${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `INR ${(n / 1000).toFixed(1)}K`;
  return `INR ${n.toLocaleString()}`;
}

function formatCompact(n) {
  if (n >= 100000) return `${(n / 100000).toFixed(0)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return n;
}

export default DashboardPage;
